import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { clientAuthService } from '../../services/clientAuthService'
import { LoadingScreen } from '../../components/common'

/**
 * ClientDashboardPage
 *
 * This page is for authenticated clients who have created accounts.
 * It fetches their client record and redirects them to the client portal
 * using their access token.
 *
 * This provides a seamless experience for clients who log in -
 * they get automatically redirected to their personalized portal.
 */
export function ClientDashboardPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadClientData = async () => {
      try {
        // Get the current authenticated user
        const { data: { user }, error: authError } = await supabase.auth.getUser()

        if (authError || !user) {
          // Not authenticated - redirect to client login
          navigate('/client/login')
          return
        }

        // Get client record by user_id
        const client = await clientAuthService.getClientByUserId(user.id)

        if (client) {
          // Redirect to portal with their access token
          navigate(`/c/${client.accessToken}`, { replace: true })
        } else {
          // User is authenticated but not linked to a client record
          // This could happen if:
          // 1. They signed up as a provider
          // 2. Their client record was deleted
          // 3. Some other data inconsistency
          setError(
            'No client record found for this account. If you are a provider, please use the main login.'
          )
        }
      } catch (err) {
        console.error('Error loading client data:', err)
        setError('Failed to load your data. Please try again.')
      }
    }

    loadClientData()
  }, [navigate])

  // Show error state if we can't redirect
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <svg
            className="w-16 h-16 text-orange-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Account Issue
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/client/login')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Login
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Provider Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show loading while we fetch client data and redirect
  return <LoadingScreen message="Loading your dashboard..." />
}
